import type { MouseEvent } from "react";
import { ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";

// Vote counts on the public board run into the thousands; 1.2k keeps the
// control the same width no matter how popular a raise gets.
function formatCount(count: number): string {
  if (count < 1000) return String(count);
  const k = count / 1000;
  return `${k >= 10 ? Math.round(k) : k.toFixed(1).replace(/\.0$/, "")}k`;
}

/**
 * ProductHunt-style upvote control. Filled when the current user has voted.
 *
 * `solid` is the in-product treatment. `outline` is the public Raiseboard
 * treatment: a quiet bordered control that sits on white cards without
 * competing with the primary action.
 */
export function UpvoteButton({
  count,
  voted,
  onClick,
  variant = "solid",
}: {
  count: number;
  voted: boolean;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  variant?: "solid" | "outline";
}) {
  const outline = variant === "outline";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={voted ? "Remove upvote" : "Upvote"}
      aria-pressed={voted}
      className={cn(
        "flex shrink-0 cursor-pointer flex-col items-center justify-center border transition-[background-color,border-color,color,transform] duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 active:scale-[0.96]",
        outline ? "h-12 w-11 gap-0 rounded-lg" : "h-14 w-14 gap-0.5 rounded-xl",
        voted
          ? "border-foreground bg-foreground text-background"
          : outline
            ? "border-border bg-card text-foreground hover:border-foreground/30"
            : "border-primary/40 bg-primary/5 text-primary hover:bg-primary hover:text-primary-foreground"
      )}
    >
      <ChevronUp
        className={outline ? "h-4 w-4" : "h-5 w-5"}
        strokeWidth={outline ? 2.25 : 2.5}
      />
      <span
        className={cn(
          "tabular-nums",
          outline ? "text-[12px] font-semibold" : "font-mono text-xs font-bold"
        )}
      >
        {outline ? formatCount(count) : count}
      </span>
    </button>
  );
}
